import { SlidersVertical } from "lucide-react"
import { FilterT } from "shared-ts/hooks/useFilter/types/filter.types"
import { matchingPreset } from "shared-ts/libs/dates/presets"
import { TimestampRange } from "shared-ts/libs/dates/timestampRange"

import { ButtonProps } from "../../../../../components/base/control/Button"
import { DropdownButton } from "../../DropdownButton/DropdownButton"

interface FilterButtonProps extends Omit<ButtonProps, "mode" | "tone"> {
  filter: FilterT | null
}

export const FilterButton = ({ filter, onClick, ref }: FilterButtonProps) => {
  const hideDropdownIcon = !filter
  const label = getFilterLabel(filter)
  const isSelected =
    !!filter &&
    ((filter.type === "date" && !!filter.selectedDateRange) ||
      (filter?.type === "text" && filter.selectedOptions.length > 0))

  return (
    <DropdownButton
      ref={ref}
      mode="ghost"
      selected={isSelected}
      icon={SlidersVertical}
      label={label}
      hideDropdownIcon={hideDropdownIcon}
      onClick={onClick}
      size="small"
    />
  )
}

const MAX_VALUES_DISPLAYED = 3

export function getFilterLabel(filter: FilterT | null) {
  if (!filter) {
    return "Filter"
  }

  if (filter.type === "date") {
    if (!filter.selectedDateRange) {
      return filter.value
    }

    const selectedPreset = matchingPreset(
      filter.presetsToDisplay,
      filter.selectedDateRange
    )

    const humanizedDate = selectedPreset
      ? selectedPreset.displayText
      : TimestampRange.humanize(filter.selectedDateRange.toTimestampRangePB())
    if (filter.value.toLowerCase() === "date") {
      return humanizedDate
    }

    return `${filter.value}: ${humanizedDate}`
  }

  let values = filter.selectedOptions.map((o) => o.value)
  if (values.length === 0) {
    return filter.value
  }

  if (values.length > MAX_VALUES_DISPLAYED) {
    const lengthOverflow = values.length - MAX_VALUES_DISPLAYED
    const overflowText = lengthOverflow > 1 ? "others" : "other"
    values = values.slice(0, MAX_VALUES_DISPLAYED)
    values.push(`and ${lengthOverflow} ${overflowText}`)
  }

  const valueText = values.join(", ")

  return `${filter.value}: ${valueText}`
}
